import React, { useState } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../../../firebase";

const BillingPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);

  const openBillingPortal = async () => {
    setIsLoading(true);
    try {
      const result = await httpsCallable(
        functions,
        "generateBillingPortalSession"
      )({
        redirectUrl: window.location.href,
      });
      const data = result.data as { url: string };
      window.open(data.url, "_blank");
    } catch (err) {
      console.error("Error generating billing portal session:", err);
      alert("Failed to open billing portal. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Current Plan */}
      <div className="bg-white border border-gallery-200 rounded-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gallery-100">
          <h2 className="font-display text-lg text-gallery-900">Current Plan</h2>
          <p className="text-sm text-gallery-500 mt-1">
            Your subscription details and usage
          </p>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="p-5 bg-gallery-50 rounded-sm border border-gallery-100">
              <p className="text-xs font-medium uppercase tracking-wider text-gallery-500 mb-2">
                Plan
              </p>
              <p className="font-display text-2xl text-gallery-900">Professional</p>
            </div>

            <div className="p-5 bg-gallery-50 rounded-sm border border-gallery-100">
              <p className="text-xs font-medium uppercase tracking-wider text-gallery-500 mb-2">
                API Requests
              </p>
              <p className="font-display text-2xl text-gallery-900">Unlimited</p>
            </div>

            <div className="p-5 bg-gallery-50 rounded-sm border border-gallery-100">
              <p className="text-xs font-medium uppercase tracking-wider text-gallery-500 mb-2">
                Status
              </p>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 bg-emerald-500 rounded-full" />
                <p className="font-display text-2xl text-gallery-900">Active</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Billing Management */}
      <div className="bg-white border border-gallery-200 rounded-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gallery-100">
          <h2 className="font-display text-lg text-gallery-900">Billing Management</h2>
          <p className="text-sm text-gallery-500 mt-1">
            Manage your payment methods and view invoices
          </p>
        </div>

        <div className="p-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <button
              onClick={openBillingPortal}
              disabled={isLoading}
              className="inline-flex items-center justify-center px-6 py-3 bg-gallery-900 text-white text-sm font-medium rounded-sm hover:bg-gallery-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? (
                <>
                  <svg className="w-4 h-4 mr-2 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Opening Portal...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                  </svg>
                  Manage Payment Methods
                </>
              )}
            </button>

            <button
              onClick={openBillingPortal}
              disabled={isLoading}
              className="inline-flex items-center justify-center px-6 py-3 bg-white text-gallery-700 text-sm font-medium rounded-sm border border-gallery-200 hover:bg-gallery-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              View Invoices
            </button>
          </div>

          <p className="text-sm text-gallery-500 mt-4">
            You will be redirected to our secure billing portal powered by Stripe.
          </p>
        </div>
      </div>

      {/* Usage & Limits */}
      <div className="bg-white border border-gallery-200 rounded-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gallery-100">
          <h2 className="font-display text-lg text-gallery-900">Usage & Limits</h2>
        </div>

        <div className="p-6 space-y-6">
          <div>
            <div className="flex justify-between items-center mb-2">
              <p className="text-sm font-medium text-gallery-700">API Requests (This Month)</p>
              <p className="text-sm text-gallery-500">No limit</p>
            </div>
            <div className="h-2 bg-gallery-100 rounded-full overflow-hidden">
              <div className="h-full bg-emerald-500 rounded-full" style={{ width: "25%" }} />
            </div>
            <p className="text-xs text-gallery-400 mt-1.5">~2,500 requests used</p>
          </div>

          <div>
            <div className="flex justify-between items-center mb-2">
              <p className="text-sm font-medium text-gallery-700">AI Valuations (This Month)</p>
              <p className="text-sm text-gallery-500">100 / month</p>
            </div>
            <div className="h-2 bg-gallery-100 rounded-full overflow-hidden">
              <div className="h-full bg-blue-500 rounded-full" style={{ width: "45%" }} />
            </div>
            <p className="text-xs text-gallery-400 mt-1.5">45 valuations used</p>
          </div>
        </div>
      </div>

      {/* Support */}
      <div className="bg-gallery-100 rounded-sm p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <p className="font-medium text-gallery-900">Need help with billing?</p>
            <p className="text-sm text-gallery-600 mt-1">
              Contact our support team for assistance with your subscription.
            </p>
          </div>
          <a
            href="mailto:support@artcollection.io"
            className="inline-flex items-center justify-center px-5 py-2.5 bg-white text-gallery-700 text-sm font-medium rounded-sm border border-gallery-200 hover:bg-gallery-50 transition-colors"
          >
            Contact Support
          </a>
        </div>
      </div>
    </div>
  );
};

export default BillingPage;
